import { assessmentQuestionnaireApiHandler } from '@api/assessmentQuestionnaireApiHandler';
import { isEmptyValidation } from '@helpers/validation.helper';
import { useAppSelector } from '@hooks/useAppSelector';
import useInput, { getInputValue } from '@hooks/useInput';
import { CircularProgress, TextField } from '@mui/material';
import { MotionText, showToast } from '@shared/atoms';
import { SystemButton } from '@shared/molecules';
import { useMutation } from '@tanstack/react-query';
import { ToastMessages } from 'constants/toastMessages';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import styles from './styles.module.scss';

export const ReflectionSection: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const questionnaireId = params.get('questionnaireId');
  const { chartSelectedAssessment } = useAppSelector((state) => state.assessmentScores || {});
  const {
    value: reflectionText,
    isValid: reflectionTextIsValid,
    hasError: reflectionTextHasError,
    valueChangeHandler: reflectionTextChangeHandler,
    inputBlurHandler: reflectionTextBlurHandler,
    reset: reflectionTextReset,
  } = useInput(isEmptyValidation, getInputValue, '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const characterCountRef = useRef<HTMLParagraphElement>(null);
  const statusMessageRef = useRef<HTMLDivElement>(null);
  const MAX_CHARS = 2000;

  useEffect(() => {
    const characterCount = reflectionText?.length || 0;
    if (characterCount % 100 === 0 && characterCount > 0) {
      if (statusMessageRef.current) {
        statusMessageRef.current.textContent = `${characterCount} of 2000 characters used`;
      }
    }
  }, [reflectionText]);

  const useSubmitReflection = () =>
    useMutation({
      mutationFn: ({
        assessmentId,
        reflection,
      }: {
        assessmentId: number | null;
        reflection: string;
      }) => {
        const payload = {
          assessmentId,
          reflection,
        };

        return assessmentQuestionnaireApiHandler.createReflection(payload);
      },
    });

  const submitReflectionMutation = useSubmitReflection();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (reflectionTextIsValid) {
      setIsSubmitting(true);

      if (statusMessageRef.current) {
        statusMessageRef.current.textContent = 'Sending your reflection...';
      }

      submitReflectionMutation.mutate(
        {
          assessmentId: chartSelectedAssessment?.assessmentId || null,
          reflection: reflectionText,
        },
        {
          onSuccess: (response) => {
            if (response?.success) {
              if (statusMessageRef && statusMessageRef?.current) {
                statusMessageRef.current.textContent =
                  'Your reflection has been saved successfully.';
              }

              reflectionTextReset();

              showToast(
                ToastMessages.journal.success.REFLECTION_SAVED_AND_SHARED,
                undefined,
                'success',
              );
            } else {
              if (statusMessageRef && statusMessageRef?.current) {
                statusMessageRef.current.textContent =
                  'Error submitting reflection. Please try again.';
              }

              showToast(ToastMessages.journal.error.ERROR_SUBMITTING, undefined, 'error');
            }
          },
          onError: (error: any) => {
            showToast(ToastMessages.journal.error.ERROR_SUBMITTING, undefined, 'error');
          },
          onSettled: () => {
            setTimeout(() => {
              setIsSubmitting(false);
            }, 1000);
          },
        },
      );
    } else {
      if (statusMessageRef && statusMessageRef?.current) {
        statusMessageRef.current.textContent = 'Please enter your reflection before submitting.';
      }

      showToast(ToastMessages.journal.error.PLEASE_ENTER_YOUR_REFLECTION, undefined, 'error');
    }
  };

  return (
    <div className={styles.reflectionsContainer}>
      <div className={styles.topContainer}>
        <MotionText
          id="reflections-heading"
          as="h3"
          text="Your journal"
          className={styles.sectionTitle}
        />

        <div className={styles.viewActionsButton}>
          <SystemButton
            onClick={() => history.push('/journals')}
            size="medium"
            className={styles.journalNavBtn}
          >
            Your Journal
          </SystemButton>
        </div>
      </div>

      <MotionText
        as="text"
        text="Take a moment to reflect on your assessment results. What insights stand out to you? What
          might be influencing your scores?"
        className={styles.sectionDescription}
      />

      <div ref={statusMessageRef} className="visually-hidden" role="status" aria-live="polite" />

      <div className={styles.inputContainer}>
        <TextField
          id="reflection-text"
          label=""
          placeholder="Write your personal private reflection here..."
          multiline
          rows={4}
          value={reflectionText}
          fullWidth
          onBlur={reflectionTextBlurHandler}
          onChange={(e) => {
            if (e.target.value.length <= MAX_CHARS) {
              reflectionTextChangeHandler(e);
            }
          }}
          inputProps={{
            'aria-describedby': 'char-counter reflection-hint',
            maxLength: MAX_CHARS,
          }}
          error={reflectionTextHasError}
        />
        <div id="reflection-hint" className="visually-hidden">
          Share your thoughts or feelings in this reflection area. Limited to 2000 characters.
        </div>

        <p
          className={styles.hsCharCounter}
          id="char-counter"
          ref={characterCountRef}
          aria-live="polite"
        >
          {reflectionText?.length || 0}/2000 characters
        </p>
      </div>

      <div className={styles.continueBtn}>
        <SystemButton
          type="button"
          onClick={() =>
            handleSubmit(new Event('submit') as unknown as React.MouseEvent<HTMLButtonElement>)
          }
          variant="outlinedPrimarySkyBlue"
          className={styles.hsPrimaryButton}
          disabled={
            reflectionTextHasError ||
            reflectionText?.length === 0 ||
            !reflectionText ||
            isSubmitting
          }
          aria-busy={isSubmitting}
        >
          &nbsp;&nbsp;&nbsp;
          {isSubmitting ? (
            <>
              <CircularProgress size={20} color="inherit" aria-hidden="true" /> &nbsp;Sending...
            </>
          ) : (
            'Save'
          )}
          &nbsp;&nbsp;&nbsp;
        </SystemButton>
      </div>
    </div>
  );
};
